import useSelector from '../../../../infrastructure/useSelector';
import { shallowEqual } from 'react-redux';
import {
    useGetAnalyticsSourcesQuery,
    useGetFeatureFlagsQuery,
    useGetHasRequiredWorkstationQuery,
    useGetSettingsQuery
} from '../../../../../generated/apollo-hooks';
import { AnalyticsSource, FeatureFlagKeys } from '@communication-center/data-objects';
import isNil from 'lodash/isNil';

export const useUserSettings = ({ onAnalyticsSourceError }: { onAnalyticsSourceError?: $TSFixMe } = {}) => {
    const { supportedSystemType, systemId, environmentId, authorized } = useSelector(
        state => ({
            supportedSystemType: state.common.supportedSystemType,
            systemId: state.common.currentSystem,
            environmentId: state.common?.currentEnvironment?.id,
            authorized: state.common.authorized
        }),
        shallowEqual
    );

    const skip = !(authorized && supportedSystemType);

    const { data: featureFlagsData, error: featureFlagsError  } = useGetFeatureFlagsQuery({
        variables: { systemId },
        skip,
    });

    const { data: hasRequiredWorkstationData, error: hasRequiredWorkstationError } = useGetHasRequiredWorkstationQuery({
        variables: { systemId, environmentId },
        skip
    });

    const { data: useGetAnalyticsSourcesData, error: useGetAnalyticsSourcesError } = useGetAnalyticsSourcesQuery({
        variables: { systemId, environmentId },
        skip,
        fetchPolicy: 'cache-first',
        onError: error => {
            onAnalyticsSourceError?.(error);
        }
    });

    const { data: settingsData, error: settingsError } = useGetSettingsQuery({ variables: { systemId }, skip });
    // console.log({featureFlagsData, systemId})

    return {
        featureFlags: featureFlagsData?.getUserSettings?.featureFlags,
        hasRequiredWorkstation: hasRequiredWorkstationData?.getUserSettings?.hasRequiredWorkstation,
        analyticsSources: useGetAnalyticsSourcesData?.getUserSettings?.analyticsSources,
        settings: settingsData?.getUserSettings?.settings,
        error: {
            featureFlags: featureFlagsError,
            hasRequiredWorkstation: hasRequiredWorkstationError,
            analyticsSources: useGetAnalyticsSourcesError,
            settings: settingsError
        }
    };
};

export const useElectronAnalytics = () => {
    const { analyticsSources, error } = useUserSettings();
    const forcedAnalyticsSource = useSelector(state => state.common.forcedAnalyticsSource);
    const isLegacyAnalyticsDisabled = useFeatureFlag(FeatureFlagKeys.LegacyAnalyticsDisabled);

    if (isLegacyAnalyticsDisabled) return true;

    if (!isNil(forcedAnalyticsSource)) {
        return forcedAnalyticsSource === AnalyticsSource.Workstation;
    }

    if (error?.analyticsSources) {
        return true;
    }

    if (!isNil(analyticsSources)) {
        return analyticsSources.useElectronAnalytics;
    }

    return null;
};

export const _filterFeatureFlags = (featureFlags: $TSFixMe) =>
    featureFlags
        ? Object.entries(featureFlags).reduce((prev, [key, value]) => {
              if (typeof value === 'boolean') {
                  prev[key] = value;
              }

              return prev;
          }, {})
        : null;

// @ts-expect-error ts-migrate(7006) FIXME: Parameter 'accountFeatureFlags' implicitly has an ... Remove this comment to see the full error message
export const _mergeFeatureFlags = (accountFeatureFlags, overrideFeatureFlags) => ({
    ..._filterFeatureFlags(accountFeatureFlags),
    ..._filterFeatureFlags(overrideFeatureFlags)
});

export const useFeatureFlags = () => {
    const { featureFlags } = useUserSettings();

    const overrideFeatureFlags = useSelector(state => state.common.overrideFeatureFlags);

    return _mergeFeatureFlags(featureFlags, overrideFeatureFlags);
};

export const useFeatureFlag = (featureFlag: FeatureFlagKeys) => {
    const featureFlags = useFeatureFlags();
    return featureFlags[featureFlag];
};
